import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck, Ban, CalendarClock, CircleSlash, DoorClosed, IndianRupee, Lock,
  MapPin, ReceiptText, ShieldAlert, UserRound, UserX,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { api, fmtINR, fmtDate, istDateISO } from "../lib/api";

// Phase 97: visit clock time is IST — stored fields first, else convert the UTC created_at
export const visitWhenIST = (v) => {
  if (!v) return "-";
  if (v.visit_date_ist) return `${fmtDate(v.visit_date_ist)} ${(v.visit_time_ist || "").slice(0, 5)} IST`.trim();
  if (!v.created_at) return "-";
  const d = new Date(v.created_at);
  const clock = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
  return `${fmtDate(istDateISO(d))} ${clock} IST`;
};
export const visitDayIST = (v) => (v?.visit_date_ist ? fmtDate(v.visit_date_ist) : v?.created_at ? fmtDate(istDateISO(new Date(v.created_at))) : "-");
import { StaffLabel } from "./StaffAvatar";
import { AuthedImg } from "./GpsPhotoGallery";
import { useReceiptVisibility } from "../lib/receiptVisibility";   // Phase 169
import { ReceiptHiddenBadge } from "./ReceiptVisibilityNotice";   // Phase 169

// Phase 72 — visit outcome registry (kept in sync with backend VISIT_OUTCOMES)
export const VISIT_OUTCOME_META = {
  full_payment: { label: "Full payment collected", icon: BadgeCheck, tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  partial_payment: { label: "Partial payment collected", icon: IndianRupee, tone: "text-sky-700 bg-sky-50 border-sky-200" },
  no_payment: { label: "No payment — reason recorded", icon: CircleSlash, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  refused: { label: "Owner refused to pay", icon: Ban, tone: "text-red-700 bg-red-50 border-red-200" },
  locked: { label: "Property locked / owner absent", icon: Lock, tone: "text-slate-600 bg-slate-50 border-slate-200" },
  preferred_date: { label: "Owner gave preferred collection date", icon: CalendarClock, tone: "text-sky-700 bg-sky-50 border-sky-200" },
  next_visit: { label: "Not paying today — next visit scheduled", icon: CalendarClock, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  closed: { label: "Property closed", icon: DoorClosed, tone: "text-slate-600 bg-slate-50 border-slate-200" },
  absent: { label: "Owner absent", icon: UserX, tone: "text-slate-600 bg-slate-50 border-slate-200" },
  // Phase 225: the receipt behind a payment visit was deleted -> the visit stays visible (outside the reports) but is marked
  receipt_deleted: { label: "Receipt deleted — payment not counted", icon: ReceiptText, tone: "text-rose-700 bg-rose-50 border-rose-200" },
};

/** Phase 225 — true when the receipt behind this visit was deleted / purged (the visit is hidden from the reports). */
export const isReceiptDeleted = (v) => !!(v && (v.receipt_deleted || v.outcome === "receipt_deleted" || v.payment_deleted === true || v.payment_purged === true || v.payment_missing === true));

/** Phase 225 — "Receipt deleted" badge (title carries the deleted receipt number / amount when known). */
export function ReceiptDeletedBadge({ v, testId, className = "" }) {
  const d = v?.deleted_receipt || {};
  const bits = [d.receipt_number ? `Receipt ${d.receipt_number}` : null, d.amount ? fmtINR(d.amount) : null].filter(Boolean).join(" • ");
  return (
    <Badge variant="outline" className={`text-[10px] gap-1 bg-rose-50 text-rose-700 border-rose-200 ${className}`} data-testid={testId}
      title={`${bits ? bits + " — " : ""}this receipt was deleted; the payment is not counted and the visit is hidden from the Visit Report and MIS reports`}>
      <ReceiptText className="h-3 w-3" aria-hidden="true" /> Receipt deleted
    </Badge>
  );
}

// Outcomes staff can pick manually in the visit drawer (payment outcomes are auto-logged)
export const MANUAL_VISIT_OUTCOMES = [
  { value: "no_payment", label: "No payment — record reason", needsReason: true },
  { value: "preferred_date", label: "Owner gave preferred collection date", needsDate: true },
  { value: "next_visit", label: "Not paying today — schedule next visit", needsDate: true },
  { value: "refused", label: "Owner refused to pay" },
  { value: "locked", label: "Property locked / owner absent" },
  { value: "closed", label: "Property closed" },
  { value: "absent", label: "Owner absent" },
];

export const outcomeLabel = (v) => VISIT_OUTCOME_META[v.outcome]?.label || v.outcome_label || v.outcome;

const ROLE_BADGE = {
  supervisor: { label: "Supervisor", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  municipal_admin: { label: "Admin", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  tax_collector: { label: "Collector", cls: "bg-sky-100 text-sky-800 border-sky-200" },
};

export function RoleBadge({ role }) {
  const meta = ROLE_BADGE[role] || ROLE_BADGE.tax_collector;
  return (
    <Badge variant="outline" className={`text-[10px] ${meta.cls}`} data-testid={`visit-role-badge-${role}`}>
      {meta.label}
    </Badge>
  );
}

/** Fetch the visit history for a property (returns null while loading / on failure). */
export function useVisitHistory(code, propId) {
  const [history, setHistory] = useState(null);
  const load = useCallback(() => {
    if (!propId) return Promise.resolve();
    return api.get(`/t/${code}/visits/property/${propId}`)
      .then((r) => setHistory(r.data))
      .catch(() => setHistory(null));
  }, [code, propId]);
  useEffect(() => { load(); }, [load]);
  return { history, reload: load };
}

/** Amber callout shown to collectors when a supervisor/admin already visited this property. */
export function SupervisorVisitBanner({ summary }) {
  const v = summary?.last_supervisor_visit;
  if (!v) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2 text-amber-900"
      data-testid="supervisor-visit-banner">
      <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="text-xs space-y-0.5">
        <p className="font-semibold">
          {v.visited_by_role === "municipal_admin" ? "Admin" : "Supervisor"} {v.visited_by_name} already visited this property
          {v.created_at || v.visit_date_ist ? ` on ${visitDayIST(v)}` : ""}
        </p>
        <p data-testid="supervisor-visit-banner-outcome">{outcomeLabel(v)}{!isReceiptDeleted(v) && v.amount_collected ? ` • ${fmtINR(v.amount_collected)} collected (receipt ${v.receipt_number})` : ""}</p>
        {v.notes && <p className="italic" data-testid="supervisor-visit-banner-remarks">Remarks: {v.notes}</p>}
        <p className="text-amber-700">
          {summary.supervisor_visit_count} supervisor visit{summary.supervisor_visit_count === 1 ? "" : "s"} • {summary.total_visits} total visit{summary.total_visits === 1 ? "" : "s"} on this property
        </p>
      </div>
    </div>
  );
}

/** Phase 217 — same-day warning: shown to a TC / Supervisor who opens a property another staff member already
 *  visited today. They may continue; a visit-only log will not count in their name, a payment will. */
export function SameDayVisitBanner({ sameDay }) {
  if (!sameDay?.show_warning || !sameDay.first) return null;
  const f = sameDay.first;
  return (
    <div className="rounded-lg border border-orange-300 bg-orange-50 px-3 py-2.5 flex items-start gap-2 text-orange-900"
      role="alert" data-testid="same-day-visit-banner">
      <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="text-xs space-y-0.5">
        <p className="font-semibold" data-testid="same-day-visit-banner-text">{sameDay.message}</p>
        <p className="text-orange-800" data-testid="same-day-visit-banner-first">
          First visit today: {f.time_ist ? `${f.time_ist} IST • ` : ""}{outcomeLabel({ outcome: f.outcome, outcome_label: f.outcome_label })}
          {f.outcome !== "receipt_deleted" && f.amount_collected ? ` • ${fmtINR(f.amount_collected)} collected (receipt ${f.receipt_number})` : ""}
          {f.imported ? " • imported payment" : ""}
          {sameDay.count > 1 ? ` • ${sameDay.count} visits on this property today` : ""}
        </p>
      </div>
    </div>
  );
}

/** Read-only, role-aware visit timeline (newest first). */
export function VisitTimeline({ code, visits, emptyText = "No visits recorded yet for this property." }) {
  const rv = useReceiptVisibility(code);   // Phase 169: receipt links follow the municipality's visibility rule for this role
  if (!visits) return null;
  if (visits.length === 0) {
    return <p className="text-sm text-muted-foreground" data-testid="visit-timeline-empty">{emptyText}</p>;
  }
  return (
    <div className="space-y-0" data-testid="visit-timeline">
      {visits.map((v, i) => {
        const meta = VISIT_OUTCOME_META[v.outcome] || {};
        const Icon = meta.icon || UserRound;
        return (
          <div key={v.id} className="flex gap-3" data-testid={`visit-timeline-item-${i}`}>
            <div className="flex flex-col items-center">
              <div className={`h-7 w-7 rounded-full border flex items-center justify-center shrink-0 ${meta.tone || "text-slate-600 bg-slate-50 border-slate-200"}`}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              {i < visits.length - 1 && <div className="w-px flex-1 bg-border my-1" />}
            </div>
            <div className="pb-4 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium">{outcomeLabel(v)}</p>
                <RoleBadge role={v.visited_by_role} />
                {v.visit_number && (
                  <span className="text-[10px] text-muted-foreground font-mono" data-testid={`visit-number-${i}`}>Visit #{v.visit_number}</span>
                )}
                {isReceiptDeleted(v) && <ReceiptDeletedBadge v={v} testId={`visit-receipt-deleted-${i}`} />}
                {v.visit_counted === false && !isReceiptDeleted(v) && (
                  <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200" data-testid={`visit-not-counted-${i}`}
                    title={v.not_counted_reason === "visit_only_after_first_visitor"
                      ? `Not counted as a visit in ${v.visited_by_name}'s name — ${v.same_day_first_visitor_name || "another staff member"} had already visited that day`
                      : v.not_counted_reason === "merged_into_same_day_payment"
                        ? "Merged into this person's payment visit the same day (one counted visit per person per day)"
                        : "Already counted once for this person that day"}>
                    Not counted
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                <span data-testid={`visit-when-${i}`}>{visitWhenIST(v)}</span> • by <StaffLabel code={code} userId={v.visited_by_id || v.collector_id} name={v.visited_by_name} empCode={v.visited_by_employee_id} fallback="-" />
              </p>
              {v.notes && <p className="text-xs mt-0.5" data-testid={`visit-notes-${i}`}>{v.notes}</p>}
              {(v.expected_date || v.follow_up_date) && (
                <p className="text-xs text-amber-700 mt-0.5">
                  {v.outcome === "partial_payment" ? "Next payment expected: " : "Follow-up: "}
                  {fmtDate(v.expected_date || v.follow_up_date)}
                </p>
              )}
              {isReceiptDeleted(v) && (
                <p className="text-xs text-rose-700 mt-0.5" data-testid={`visit-receipt-deleted-note-${i}`}>
                  {v.deleted_receipt?.receipt_number ? `Receipt ${v.deleted_receipt.receipt_number}` : "The receipt"}
                  {v.deleted_receipt?.amount ? ` (${fmtINR(v.deleted_receipt.amount)})` : ""} was deleted — not counted in any report
                  {v.outstanding_after != null ? ` • dues now ${fmtINR(v.outstanding_after)}` : ""}
                </p>
              )}
              {!isReceiptDeleted(v) && v.receipt_number && (rv.canView(v.payment_date || v.visit_date_ist || v.visit_date) ? (
                <Link to={`/${code}/receipt/${v.receipt_number}`}
                  className="text-xs text-primary underline underline-offset-2" data-testid={`visit-receipt-link-${i}`}>
                  Receipt {v.receipt_number}{v.amount_collected ? ` • ${fmtINR(v.amount_collected)}` : ""}
                </Link>
              ) : (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid={`visit-receipt-hidden-${i}`}>
                  Receipt {v.receipt_number}{v.amount_collected ? ` • ${fmtINR(v.amount_collected)}` : ""} <ReceiptHiddenBadge testId={`visit-receipt-hidden-badge-${i}`} />
                </span>
              ))}
              {/* Phase 132: GPS photos that evidence this visit */}
              {(v.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`visit-photos-${i}`}>
                  {v.photos.slice(0, 4).map((p) => (
                    <div key={p.file_id} className="relative" title={p.lat != null ? `GPS ${p.lat}, ${p.lng}` : "No GPS"}>
                      <AuthedImg code={code} fileId={p.file_id} alt="visit photo" className="h-12 w-12 object-cover rounded border" />
                      {p.lat != null && (
                        <span className="absolute bottom-0 inset-x-0 flex items-center justify-center text-[8px] py-px bg-emerald-700/85 text-white rounded-b">
                          <MapPin className="h-2 w-2" />
                        </span>
                      )}
                    </div>
                  ))}
                  {v.photos.length > 4 && <span className="text-[10px] text-muted-foreground self-center">+{v.photos.length - 4}</span>}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
